import { useEffect, useState, useMemo } from 'react';
import { SellerAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler';
import { GigAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/GigAPIHandler';
import { OrderAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/OrderAPIHandler';
import { SessionManager } from '../../../../services/SessionManager/SessionManager';
import { SellerMetrics } from '../../../../Models/SellerMetrics';
import { SellerInterface } from '../../../../Models/Seller';
import Gig from '../../../../Models/Gig';
import { Order, OrderStatus } from '../../../../Models/Order';
import './DashboardOverview.scss';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Avatar, Typography, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { ErrorLogger } from '../../../../utils/Logger';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

export const DashboardOverview = () => {
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState<SellerMetrics | undefined>(undefined);
    const [seller, setSeller] = useState<SellerInterface | undefined>(undefined);
    const [gigs, setGigs] = useState<Gig[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [chartYear, setChartYear] = useState<'current' | 'previous'>('current');

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const buyer = SessionManager.shared.buyer;
                
                if (buyer && buyer.seller) {
                    // Fetch seller metrics
                    const metricsData = await SellerAPIHandler.shared.getSellerMetricsById(buyer.seller);
                    setMetrics(metricsData);

                    // Fetch seller details
                    const sellerData = await SellerAPIHandler.shared.getSellerByAuthId();
                    setSeller(sellerData);

                    // Fetch gigs
                    const gigsData = await GigAPIHandler.shared.getGigsBySeller(buyer.seller);
                    setGigs(gigsData);

                    // Fetch orders
                    const ordersData = await OrderAPIHandler.shared.getOrdersBySellerId();
                    setOrders(ordersData);
                }
            } catch (error) {
                ErrorLogger.log(error, 'DashboardOverview.fetchDashboardData');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Calculate derived metrics
    const totalEarnings = metrics ? metrics.totalEarningsInPaisa / 100 : 0;
    const totalOrders = metrics?.totalOrders || 0;
    const activeGigs = gigs.filter(gig => gig.isListed).length;
    const totalGigImpressions = gigs.reduce((sum, gig) => {
        return sum + (gig.gigMetrics?.totalOrders || 0);
    }, 0);

    // Get top performing gigs (by orders)
    const topGigs = [...gigs]
        .filter(gig => gig.isListed)
        .sort((a, b) => (b.gigMetrics?.totalOrders || 0) - (a.gigMetrics?.totalOrders || 0))
        .slice(0, 3);

    // Get top clients (unique buyers from orders)
    const clientMap = new Map<string, { buyer: any; orders: number; totalSpent: number }>();
    orders.forEach(order => {
        const buyerId = order.metaData.buyer.id;
        const existing = clientMap.get(buyerId);
        const orderTotal = order.orderSummary?.orderTotals.totalAmountPayableByBuyer || 0;
        
        if (existing) {
            existing.orders += 1;
            existing.totalSpent += orderTotal;
        } else {
            clientMap.set(buyerId, {
                buyer: order.metaData.buyer,
                orders: 1,
                totalSpent: orderTotal
            });
        }
    });
    const topClients = Array.from(clientMap.values())
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 3);

    // Get recent orders for table
    const recentOrders = [...orders]
        .sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
        })
        .slice(0, 5);

    // Calculate order status distribution
    const orderStatusCounts = {
        pending: orders.filter(o => o.status === OrderStatus.pending).length,
        readyForDelivery: orders.filter(o => o.status === OrderStatus.readyForDelivery).length,
        approvedByBuyer: orders.filter(o => o.status === OrderStatus.approvedByBuyer).length,
        completed: orders.filter(o => o.status === OrderStatus.completed).length,
        cancelled: orders.filter(o => o.status === OrderStatus.cancelled).length,
        overdue: orders.filter(o => o.status === OrderStatus.overdue).length,
    };

    // Format currency
    const formatCurrency = (amount: number) => {
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    // Format date
    const formatDate = (date: Date | undefined) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[d.getMonth()]} ${d.getDate().toString().padStart(2, '0')}, ${d.getFullYear()}`;
    };

    // Format date time for recent activity
    // const formatDateTime = (date: Date | undefined) => {
    //     if (!date) return 'N/A';
    //     const d = new Date(date);
    //     const now = new Date();
    //     const diffMs = now.getTime() - d.getTime();
    //     const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    //     const diffDays = Math.floor(diffHours / 24);
        
    //     if (diffHours < 1) return 'Just now';
    //     if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    //     if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    //     return formatDate(date);
    // };

    // Calculate percentage change
    const getPercentageChange = (current: number, previous: number = 0) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 100);
    };

    // Prepare earnings chart data (monthly data)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const earningsData = {
        labels: months,
        datasets: [
            {
                label: 'Earnings',
                data: months.map(() => 0), // Placeholder - would need actual monthly data
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    const earningsOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#ffffff',
                titleColor: '#1c1c1c',
                bodyColor: '#7c7c7c',
                borderColor: '#e7e7e7',
                borderWidth: 1,
                padding: 12,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f3f4f6',
                },
                ticks: {
                    color: '#7c7c7c',
                    font: {
                        size: 12,
                    },
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#7c7c7c',
                    font: {
                        size: 12,
                    },
                },
            },
        },
    };

    // Gig Performance chart data (bar chart)
    const gigPerformanceData = {
        labels: topGigs.length > 0 ? topGigs.map(gig => gig.title.length > 15 ? `${gig.title.substring(0, 15)}...` : gig.title) : ['No Gigs'],
        datasets: [
            {
                label: 'Views',
                data: topGigs.length > 0 ? topGigs.map(gig => (gig.gigMetrics?.totalOrders || 0) * 10) : [0], // Mock views data
                backgroundColor: '#3b82f6',
                borderRadius: 4,
            },
            {
                label: 'Orders',
                data: topGigs.length > 0 ? topGigs.map(gig => gig.gigMetrics?.totalOrders || 0) : [0],
                backgroundColor: '#22c55e',
                borderRadius: 4,
            },
        ],
    };

    const gigPerformanceOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    padding: 15,
                    font: {
                        size: 12,
                    },
                    color: '#1c1c1c',
                    usePointStyle: true,
                    pointStyle: 'rect',
                },
            },
            tooltip: {
                backgroundColor: '#ffffff',
                titleColor: '#1c1c1c',
                bodyColor: '#7c7c7c',
                borderColor: '#e7e7e7',
                borderWidth: 1,
                padding: 12,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f3f4f6',
                },
                ticks: {
                    color: '#7c7c7c',
                    font: {
                        size: 12,
                    },
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#7c7c7c',
                    font: {
                        size: 12,
                    },
                },
            },
        },
    };

    // Order status pie chart data
    const orderStatusData = {
        labels: ['Completed', 'In Progress', 'Pending'],
        datasets: [
            {
                data: [
                    orderStatusCounts.completed,
                    orderStatusCounts.approvedByBuyer + orderStatusCounts.readyForDelivery,
                    orderStatusCounts.pending,
                ],
                backgroundColor: [
                    '#22c55e',
                    '#3b82f6',
                    '#f97316',
                ],
                borderWidth: 0,
            },
        ],
    };

    const orderStatusOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    padding: 15,
                    font: {
                        size: 12,
                    },
                    color: '#1c1c1c',
                },
            },
            tooltip: {
                backgroundColor: '#ffffff',
                titleColor: '#1c1c1c',
                bodyColor: '#7c7c7c',
                borderColor: '#e7e7e7',
                borderWidth: 1,
                padding: 12,
            },
        },
    };

    // Get status badge color
    const getStatusColor = (status: OrderStatus) => {
        switch (status) {
            case OrderStatus.completed:
                return '#22c55e';
            case OrderStatus.approvedByBuyer:
            case OrderStatus.readyForDelivery:
                return '#3b82f6';
            case OrderStatus.pending:
                return '#f97316';
            case OrderStatus.cancelled:
                return '#ef4444';
            case OrderStatus.overdue:
                return '#dc2626';
            default:
                return '#7c7c7c';
        }
    };

    // Get status label
    const getStatusLabel = (status: OrderStatus) => {
        switch (status) {
            case OrderStatus.completed:
                return 'Completed';
            case OrderStatus.approvedByBuyer:
                return 'Approved';
            case OrderStatus.readyForDelivery:
                return 'Ready for Delivery';
            case OrderStatus.pending:
                return 'Pending';
            case OrderStatus.cancelled:
                return 'Cancelled';
            case OrderStatus.overdue:
                return 'Overdue';
            default:
                return status;
        }
    };

    if (loading) {
        return (
            <div className="dashboard-overview">
                <div className="dashboard-overview-skeleton">
                    <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: '1rem' }} />
                    <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '1rem' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-overview">
            {/* Welcome Section */}
            <div className="welcome-section">
                <Typography variant="subtitle1" className="welcome-title">
                    Welcome Back, {seller?.fullName || 'Freelancer'}!
                </Typography>
                <Typography variant="body1" className="welcome-subtitle">
                    Here's what's happening with your freelance business today
                </Typography>
            </div>

            {/* Summary Cards */}
            <div className="summary-cards-grid">
                <SummaryCard
                    title="Total Earnings"
                    value={formatCurrency(totalEarnings)}
                    change={getPercentageChange(totalEarnings, 0)}
                    icon={<AccountBalanceWalletIcon />}
                    color="blue"
                />
                <SummaryCard
                    title="Total Orders"
                    value={totalOrders.toString()}
                    change={getPercentageChange(totalOrders, 0)}
                    icon={<ShoppingCartIcon />}
                    color="green"
                />
                <SummaryCard
                    title="Active Gigs"
                    value={activeGigs.toString()}
                    change={0}
                    icon={<AutoAwesomeIcon />}
                    color="purple"
                />
                <SummaryCard
                    title="Gigs Clicks"
                    value={totalGigImpressions.toLocaleString()}
                    change={getPercentageChange(totalGigImpressions, 0)}
                    icon={<VisibilityIcon />}
                    color="orange"
                    showBeta={true}
                />
            </div>

            {/* Main Content Grid */}
            <div className="dashboard-content-grid">
                {/* Top Row: Earnings Chart + Orders Pie Chart - Full Width */}
                <div className="dashboard-row-top">
                    {/* Earnings Chart */}
                    <div className="dashboard-card earnings-card">
                        <div className="card-header">
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Typography variant="h6" className="card-title">Earnings</Typography>
                                    <span className="beta-badge">Beta</span>
                                </div>
                                <Typography variant="body2" className="card-subtitle">Monthly revenue comparison</Typography>
                            </div>
                            <div className="chart-toggle">
                                <button 
                                    className={`toggle-btn ${chartYear === 'current' ? 'active' : ''}`}
                                    onClick={() => setChartYear('current')}
                                >
                                    Current Year
                                </button>
                                <button 
                                    className={`toggle-btn ${chartYear === 'previous' ? 'active' : ''}`}
                                    onClick={() => setChartYear('previous')}
                                >
                                    Previous Year
                                </button>
                            </div>
                        </div>
                        <div className="chart-container earnings-chart-container">
                            <Line data={earningsData} options={earningsOptions} />
                        </div>
                    </div>

                    {/* Orders Pie Chart */}
                    <div className="dashboard-card pie-chart-card">
                        <div className="card-header">
                            <Typography variant="h6" className="card-title">Orders</Typography>
                            <Typography variant="body2" className="card-subtitle">Current distribution</Typography>
                        </div>
                        <div className="chart-container pie-chart-container">
                            <Doughnut data={orderStatusData} options={orderStatusOptions} />
                        </div>
                    </div>
                </div>

                {/* Middle Row: Recent Orders Table (70-80%) + Gig Performance Chart (20-30%) */}
                <div className="dashboard-row-middle">
                    {/* Recent Orders Table - Left, Wider */}
                    <div className="dashboard-card table-card">
                        <div className="card-header">
                            <Typography variant="h6" className="card-title">Recent Orders</Typography>
                            <button className="see-all-btn">See all</button>
                        </div>
                        <TableContainer component={Paper} elevation={0} className="table-container">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="table-header">Order ID</TableCell>
                                        <TableCell className="table-header">Client</TableCell>
                                        <TableCell className="table-header">Gig</TableCell>
                                        <TableCell className="table-header">Amount</TableCell>
                                        <TableCell className="table-header">Status</TableCell>
                                        <TableCell className="table-header">Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recentOrders.length > 0 ? (
                                        recentOrders.map((order) => (
                                            <TableRow key={order.id} className="table-row">
                                                <TableCell className="table-cell">
                                                    <Typography variant="body2" className="order-id">
                                                        #{order.id.substring(0, 8)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <div className="client-cell">
                                                        <Avatar 
                                                            src={order.metaData.buyer.profileImage}
                                                            sx={{ width: 24, height: 24 }}
                                                        >
                                                            {order.metaData.buyer.fullName?.charAt(0) || 'C'}
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {order.metaData.buyer.fullName || 'Client'}
                                                        </Typography>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <Typography variant="body2" className="gig-name">
                                                        {order.metaData.gig.title.length > 25 
                                                            ? `${order.metaData.gig.title.substring(0, 25)}...` 
                                                            : order.metaData.gig.title}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <Typography variant="body2" className="amount-cell">
                                                        {formatCurrency((order.orderSummary?.orderTotals.totalAmountPayableByBuyer || 0) / 100)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <span 
                                                        className="status-badge"
                                                        style={{ backgroundColor: getStatusColor(order.status) + '20', color: getStatusColor(order.status) }}
                                                    >
                                                        {getStatusLabel(order.status)}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <Typography variant="body2" className="date-cell">
                                                        {formatDate(order.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" className="empty-state">
                                                <Typography variant="body2" color="text.secondary">
                                                    No orders yet
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    {/* Gig Performance Chart - Right, Narrower */}
                    <div className="dashboard-card gig-performance-card">
                        <div className="card-header">
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Typography variant="h6" className="card-title">Gig Performance</Typography>
                                    <span className="beta-badge">Beta</span>
                                </div>
                                <Typography variant="body2" className="card-subtitle">Views and conversion metrics</Typography>
                            </div>
                        </div>
                        <div className="chart-container gig-performance-chart-container">
                            <Bar data={gigPerformanceData} options={gigPerformanceOptions} />
                        </div>
                    </div>
                </div>

                {/* Right Column Container - Full Width Row */}
                <div className="right-column-wrapper">
                    <div className="right-column-container">
                    {/* Top Performing Gigs */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <Typography variant="h6" className="card-title">Top Performing Gigs</Typography>
                            <button className="see-all-btn">See all</button>
                        </div>
                        <div className="list-content">
                            {topGigs.length > 0 ? (
                                topGigs.map((gig) => (
                                    <div key={gig.id} className="list-item">
                                        <div className="list-item-icon">
                                            <AutoAwesomeIcon />
                                        </div>
                                        <div className="list-item-content">
                                            <Typography variant="body1" className="list-item-title">
                                                {gig.title.length > 30 ? `${gig.title.substring(0, 30)}...` : gig.title}
                                            </Typography>
                                            <Typography variant="body2" className="list-item-subtitle">
                                                {gig.subcategory}
                                            </Typography>
                                        </div>
                                        <div className="list-item-value">
                                            <Typography variant="body1" className="value-text">
                                                {(gig.gigMetrics?.totalOrders || 0).toLocaleString()}
                                            </Typography>
                                            <TrendingUpIcon className="trend-icon up" fontSize="small" />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" className="empty-state">
                                    No gigs yet. Create your first gig to get started!
                                </Typography>
                            )}
                        </div>
                    </div>

                    {/* Top Clients */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <Typography variant="h6" className="card-title">Top Clients</Typography>
                            <button className="see-all-btn">See all</button>
                        </div>
                        <div className="list-content">
                            {topClients.length > 0 ? (
                                topClients.map((client) => (
                                    <div key={client.buyer.id} className="list-item">
                                        <Avatar 
                                            src={client.buyer.profileImage} 
                                            className="client-avatar"
                                        >
                                            {client.buyer.fullName?.charAt(0) || 'C'}
                                        </Avatar>
                                        <div className="list-item-content">
                                            <div className="list-item-title-row">
                                                <Typography variant="body1" className="list-item-title">
                                                    {client.buyer.fullName || 'Client'}
                                                </Typography>
                                                {client.orders > 1 && (
                                                    <span className="verified-badge">✓</span>
                                                )}
                                            </div>
                                            <Typography variant="body2" className="list-item-subtitle">
                                                {client.orders} {client.orders === 1 ? 'Purchase' : 'Purchases'}
                                            </Typography>
                                        </div>
                                        <div className="list-item-value">
                                            <Typography variant="body1" className="value-text">
                                                {formatCurrency(client.totalSpent / 100)}
                                            </Typography>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" className="empty-state">
                                    No clients yet
                                </Typography>
                            )}
                        </div>
                    </div>

                    {/* Recent Orders List */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <Typography variant="h6" className="card-title">Recent Orders</Typography>
                            <button className="see-all-btn">See all</button>
                        </div>
                        <div className="list-content">
                            {recentOrders.slice(0, 3).length > 0 ? (
                                recentOrders.slice(0, 3).map((order) => (
                                    <div key={order.id} className="list-item">
                                        <div className="list-item-icon order-icon">
                                            <ShoppingCartIcon />
                                        </div>
                                        <div className="list-item-content">
                                            <Typography variant="body1" className="list-item-title">
                                                {order.metaData.gig.title.length > 30 
                                                    ? `${order.metaData.gig.title.substring(0, 30)}...` 
                                                    : order.metaData.gig.title}
                                            </Typography>
                                            <Typography variant="body2" className="list-item-subtitle">
                                                {order.metaData.gig.subcategory}
                                            </Typography>
                                        </div>
                                        <div className="list-item-value">
                                            <Typography variant="body1" className="value-text">
                                                {formatCurrency((order.orderSummary?.orderTotals.totalAmountPayableByBuyer || 0) / 100)}
                                            </Typography>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" className="empty-state">
                                    No orders yet
                                </Typography>
                            )}
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SummaryCardProps {
    title: string;
    value: string;
    change: number;
    icon: React.ReactNode;
    color: 'blue' | 'green' | 'purple' | 'orange';
    showBeta?: boolean;
}

const SummaryCard = ({ title, value, change, icon, color, showBeta = false }: SummaryCardProps) => {
    const isPositive = change > 0;
    const isNegative = change < 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isNeutral = change === 0;

    // Generate mock trend data for the sparkline (memoized to prevent re-renders)
    const trendData = useMemo(() => {
        const dataPoints = 12;
        const baseValue = 50;
        const trend = isPositive ? 1 : isNegative ? -1 : 0;
        const variation = 10;
        
        // Use a seed based on title to ensure consistent data
        const seed = title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        let random = seed;
        
        const nextRandom = () => {
            random = (random * 9301 + 49297) % 233280;
            return random / 233280;
        };
        
        return Array.from({ length: dataPoints }, (_, i) => {
            const progress = i / (dataPoints - 1);
            const trendValue = baseValue + (trend * progress * 30);
            const randomVariation = (nextRandom() - 0.5) * variation;
            return Math.max(0, trendValue + randomVariation);
        });
    }, [title, isPositive, isNegative]);
    const sparklineData = {
        labels: trendData.map(() => ''),
        datasets: [
            {
                data: trendData,
                borderColor: isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#7c7c7c',
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0.4,
                fill: false,
            },
        ],
    };

    const sparklineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
            },
        },
        scales: {
            x: {
                display: false,
            },
            y: {
                display: false,
            },
        },
        elements: {
            line: {
                borderWidth: 2,
            },
        },
    };

    return (
        <div className={`summary-card summary-card-${color}`}>
            <div className="summary-card-content">
                <Typography variant="h4" className="summary-card-value">
                    {value}
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Typography variant="body2" className="summary-card-title">
                        {title}
                    </Typography>
                    {showBeta && <span className="beta-badge">Beta</span>}
                </div>
                <div className="summary-card-change">
                    <Typography 
                        variant="body2" 
                        className={`change-text ${isPositive ? 'positive' : isNegative ? 'negative' : 'neutral'}`}
                    >
                        {isPositive ? '+' : ''}{change}%
                    </Typography>
                    <Typography variant="body2" className="change-timeframe">
                        This month
                    </Typography>
                </div>
            </div>
            <div className="summary-card-sparkline">
                <Line data={sparklineData} options={sparklineOptions} />
            </div>
        </div>
    );
};
